import {
  buildNeedleCandidates,
  findTextRangeInRootWithFallback,
  rangeMatchesNeedle,
  resolveDocumentQuoteLocateScope,
  wrapRangeWithHighlightClass,
} from '@/utils/htmlTextAnchor';
import { parseExcelCellRef } from '@/utils/excelDocumentHtml';
import { useAiPageContentApplyStore } from '@/stores/useAiPageContentApplyStore';
import { useAiPageContentEditPreviewStore } from '@/stores/useAiPageContentEditPreviewStore';

const HIGHLIGHT_CLASS = 'ai-page-quote-highlight';
const HIGHLIGHT_STYLE_ID = 'ai-page-quote-highlight-style';

export const ENG_QUOTE_ROW_PREFIX = 'eng-row:';
export const ENG_QUOTE_COL_PREFIX = 'eng-col:';

export function engQuoteRowLabel(rowIndex: number): string {
  return `${ENG_QUOTE_ROW_PREFIX}${rowIndex}`;
}

export function engQuoteColLabel(columnKey: string): string {
  return `${ENG_QUOTE_COL_PREFIX}${columnKey}`;
}

export function isEngineeringTableAxisLabel(label?: string | null): boolean {
  const s = String(label ?? '').trim();
  return s.startsWith(ENG_QUOTE_ROW_PREFIX) || s.startsWith(ENG_QUOTE_COL_PREFIX);
}

function ensureHighlightStyle() {
  if (document.getElementById(HIGHLIGHT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HIGHLIGHT_STYLE_ID;
  style.textContent = `
    .${HIGHLIGHT_CLASS} {
      background: rgba(255, 214, 102, 0.55) !important;
      outline: 2px solid rgba(255, 171, 0, 0.85);
      border-radius: 2px;
      transition: background 0.2s ease;
    }
    td.${HIGHLIGHT_CLASS},
    th.${HIGHLIGHT_CLASS} {
      outline-offset: -2px;
    }
  `;
  document.head.appendChild(style);
}

export function clearPageContentHighlights() {
  document.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`).forEach((el) => {
    const parent = el.parentNode;
    if (!parent) {
      el.classList.remove(HIGHLIGHT_CLASS);
      return;
    }
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });
  document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.tagName === 'DIV' && el.style.position === 'fixed') {
      el.remove();
      return;
    }
    el.classList.remove(HIGHLIGHT_CLASS);
  });
  try {
    useAiPageContentEditPreviewStore().clearLocateQuote();
  } catch {
    /* store may be unavailable in tests */
  }
}

function findSearchRoot(fieldPath?: string): ParentNode {
  if (fieldPath) {
    const byField = document.querySelector(`[data-ai-quote-field="${CSS.escape(fieldPath)}"]`);
    if (byField) return byField;
  }
  return document.body;
}

function markElementsAndScroll(els: HTMLElement[]): boolean {
  if (!els.length) return false;
  for (const el of els) el.classList.add(HIGHLIGHT_CLASS);
  els[0]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  return true;
}

function highlightByCellKey(cellKey: string): boolean {
  const el = document.querySelector(
    `[data-ai-quote-cell-key="${CSS.escape(cellKey)}"]`,
  );
  if (!(el instanceof HTMLElement)) return false;
  return markElementsAndScroll([el]);
}

function highlightEngineeringTableAxis(fieldPath: string | undefined, paragraphLabel: string): boolean {
  const root = findSearchRoot(fieldPath);
  if (!(root instanceof HTMLElement)) return false;

  const rowMatch = paragraphLabel.match(new RegExp(`^${ENG_QUOTE_ROW_PREFIX}(\\d+)$`));
  if (rowMatch) {
    const rowEl = root.querySelector(`[data-ai-quote-row="${rowMatch[1]}"]`);
    if (!(rowEl instanceof HTMLElement)) return false;
    const cells = Array.from(
      rowEl.querySelectorAll<HTMLElement>('td[data-ai-quote-col], th[data-ai-quote-col]'),
    );
    return markElementsAndScroll(cells.length ? cells : [rowEl]);
  }

  const colPrefix = ENG_QUOTE_COL_PREFIX;
  if (paragraphLabel.startsWith(colPrefix)) {
    const columnKey = paragraphLabel.slice(colPrefix.length);
    if (!columnKey) return false;
    const cells = Array.from(
      root.querySelectorAll<HTMLElement>(`[data-ai-quote-col="${CSS.escape(columnKey)}"]`),
    );
    return markElementsAndScroll(cells);
  }

  return false;
}

function highlightInLiveDom(
  root: HTMLElement,
  quoted: string,
  options?: { paragraphIndex?: number; paragraphLabel?: string },
): boolean {
  const scope = resolveDocumentQuoteLocateScope(quoted, options?.paragraphIndex, options?.paragraphLabel);
  const cellRef = parseExcelCellRef(scope.paragraphLabel);
  for (const candidate of buildNeedleCandidates(quoted)) {
    const range = findTextRangeInRootWithFallback(root, candidate, scope.paragraphIndex, cellRef);
    if (!range || !rangeMatchesNeedle(range, candidate)) continue;
    const mark = wrapRangeWithHighlightClass(range, HIGHLIGHT_CLASS, 'mark');
    if (!mark) continue;
    mark.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return true;
  }
  return false;
}

export type HighlightPageContentQuoteOptions = {
  paragraphIndex?: number;
  paragraphLabel?: string;
  cellKey?: string;
};

/** 在页面正文中定位并高亮与引用一致的文本片段 */
export function highlightPageContentQuote(
  quoted: string,
  fieldPath?: string,
  options?: HighlightPageContentQuoteOptions,
) {
  const needle = String(quoted ?? '').trim();
  const cellKey = options?.cellKey?.trim() || '';
  const paragraphLabel = options?.paragraphLabel?.trim() || '';
  if (!needle && !cellKey && !paragraphLabel) return false;

  clearPageContentHighlights();
  ensureHighlightStyle();

  if (cellKey && highlightByCellKey(cellKey)) return true;
  if (paragraphLabel && isEngineeringTableAxisLabel(paragraphLabel)) {
    if (highlightEngineeringTableAxis(fieldPath, paragraphLabel)) return true;
  }

  if (!needle) return false;

  if (fieldPath === 'document-body') {
    try {
      const scope = resolveDocumentQuoteLocateScope(needle, options?.paragraphIndex, options?.paragraphLabel);
      return useAiPageContentApplyStore().locateFieldQuote(needle, fieldPath, {
        paragraphIndex: scope.paragraphIndex ?? undefined,
        paragraphLabel: scope.paragraphLabel ?? undefined,
      });
    } catch {
      return false;
    }
  }

  const root = findSearchRoot(fieldPath);
  if (root instanceof HTMLElement) {
    if (highlightInLiveDom(root, needle, options)) return true;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    const parent = node.parentElement;
    if (!parent) continue;
    if (parent.closest('.ai-assistant-root, .expanded-ai-overlay, .ai-page-quote-floater')) continue;
    for (const candidate of buildNeedleCandidates(needle)) {
      const idx = node.data.indexOf(candidate);
      if (idx < 0) continue;
      wrapTextRange(node, idx, idx + candidate.length);
      return true;
    }
  }
  return false;
}

function wrapTextRange(textNode: Text, start: number, end: number) {
  const range = document.createRange();
  range.setStart(textNode, start);
  range.setEnd(textNode, end);
  const mark = document.createElement('mark');
  mark.className = HIGHLIGHT_CLASS;
  try {
    range.surroundContents(mark);
    mark.scrollIntoView({ block: 'center', behavior: 'smooth' });
  } catch {
    const rect = range.getBoundingClientRect();
    if (rect.height > 0) {
      const overlay = document.createElement('div');
      overlay.className = HIGHLIGHT_CLASS;
      overlay.style.position = 'fixed';
      overlay.style.left = `${rect.left}px`;
      overlay.style.top = `${rect.top}px`;
      overlay.style.width = `${rect.width}px`;
      overlay.style.height = `${rect.height}px`;
      overlay.style.pointerEvents = 'none';
      overlay.style.zIndex = '5999';
      document.body.appendChild(overlay);
      overlay.scrollIntoView({ block: 'center', behavior: 'smooth' });
      window.setTimeout(() => overlay.remove(), 3200);
    }
  }
}
